import { ZapFastIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DatasetRuleButton } from '@/Pages/Dataset/DatasetRulesPane';
import EmptyState from '@/components/EmptyState';
import { DatasetRulesDropdown } from '@/components/EvaluatorPopover/EvaluatorsPopover';
import {
  useFeedbackFormulas,
  useOrganizationId,
  useRunRules,
} from '@/hooks/useSwr';
import { RuleSchema } from '@/types/schema';
import { appEvaluatorPath, appOrganizationPath } from '@/utils/constants';

import CompositeScorePane from '../CompositeScorePane/CompositeScorePane';
import { RuleLogsPane } from '../Rules/RuleLogsPane';
import { EvaluatorsTable } from './EvaluatorsTable';
import {
  CompositeScoreRule,
  combineAndSortRulesWithFormulas,
  isCompositeScoreRule,
} from './utils/convertFeedbackFormulaToRule';

export interface DatasetEvaluatorsTableV2Props {
  onStartAlignEvaluator?: () => void;
}

export function DatasetEvaluatorsTableV2({
  onStartAlignEvaluator,
}: DatasetEvaluatorsTableV2Props) {
  'use no memo';
  const { datasetId } = useParams();

  const [selectedCompositeScoreRule, setSelectedCompositeScoreRule] = useState<
    CompositeScoreRule | undefined
  >(undefined);
  const [ruleLogsPaneRule, setRuleLogsPaneRule] = useState<
    RuleSchema | undefined
  >(undefined);

  const {
    data: allRules,
    isLoading,
    isValidating,
    mutate,
  } = useRunRules({ dataset_id: datasetId });

  const {
    data: formulas,
    mutate: mutateFormulas,
    isLoading: isLoadingFormulas,
    isValidating: isValidatingFormulas,
  } = useFeedbackFormulas(
    datasetId ? { dataset_id: datasetId, limit: 100, offset: 0 } : null
  );

  const organizationId = useOrganizationId();
  const navigate = useNavigate();

  // Combine rules and converted formulas, sorted by updated_at
  const combinedRules = useMemo(() => {
    return combineAndSortRulesWithFormulas(allRules ?? [], formulas);
  }, [allRules, formulas]);

  const combinedMutate = () => {
    mutate();
    mutateFormulas();
  };

  const isBusy = (!allRules && isLoading) || (!formulas && isLoadingFormulas);

  function renderEvaluators() {
    if (!datasetId) return <div>Dataset ID is required</div>;
    if (isBusy) return <LinearProgress />;

    return (
      <>
        <EvaluatorsTable
          rules={combinedRules}
          isLoading={isLoading || isLoadingFormulas}
          isValidating={isValidating || isValidatingFormulas}
          mutate={combinedMutate}
          setRuleLogsPaneRule={setRuleLogsPaneRule}
          onRowClick={(row: RuleSchema | CompositeScoreRule) => {
            if (isCompositeScoreRule(row)) {
              setSelectedCompositeScoreRule(row);
            } else {
              navigate(
                `/${appOrganizationPath}/${organizationId}/datasets/${datasetId!}/${appEvaluatorPath}/${
                  row.id
                }`
              );
            }
          }}
          emptyState={
            <EmptyState
              title="Create your first evaluator"
              description="There are no evaluators configured on this dataset. Evaluators let you evaluate experiment results"
              action={
                <DatasetRulesDropdown
                  trigger={
                    <DatasetRuleButton
                      datasetId={datasetId}
                      onClick={() => void 0}
                      variant="dataset"
                    />
                  }
                  datasetId={datasetId}
                  checkable={false}
                  hideCurrentRules={true}
                  onStartAlignEvaluator={onStartAlignEvaluator}
                />
              }
              docsLink={undefined}
              fancyIcon={false}
              icon={<ZapFastIcon />}
              className="mt-12"
              size="md"
            />
          }
        />
        <RuleLogsPane
          rule={ruleLogsPaneRule}
          open={!!ruleLogsPaneRule}
          onClose={() => setRuleLogsPaneRule(undefined)}
        />
        <CompositeScorePane
          open={!!selectedCompositeScoreRule}
          onClose={() => setSelectedCompositeScoreRule(undefined)}
          datasetId={datasetId}
          initialFormula={selectedCompositeScoreRule?.formula}
          onSaved={() => {
            combinedMutate();
          }}
        />
      </>
    );
  }

  return <div className="flex flex-col gap-4">{renderEvaluators()}</div>;
}
